import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import urlTemplate from 'url-template';
import type {
  CapacityManagementRootResponse,
  TradeResponse,
} from 'src/apis/capacity-management/types';
import type { CapacityManagementRootRequest } from 'src/apis/capacity-management/use-capacity-management-root';
import { useAxios } from 'src/hooks/use-axios';
import { Forbidden } from 'src/utils/errors';

type HookConfig = {
  capacityManagementRoot: CapacityManagementRootRequest;
};

type RequestOptions = {
  rootData: CapacityManagementRootResponse;
  id: string;
};

export function useTrade({ capacityManagementRoot }: HookConfig) {
  const trade = useAxios(
    (axios, baseConfig, { rootData, id }: RequestOptions) => {
      if (!rootData._links?.trade) throw new Forbidden();

      const url = urlTemplate
        .parse(rootData._links.trade.href)
        .expand({ tradeId: id });

      return axios.request<TradeResponse>({
        ...baseConfig,
        url,
      });
    },
    { neededOnPageLoad: true }
  );

  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    if (!capacityManagementRoot.response) return;

    const rootData = capacityManagementRoot.response.data;
    const options = { rootData, id };

    trade.execute(options);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capacityManagementRoot.response, id]);

  return trade;
}

export type TradeRequest = ReturnType<typeof useTrade>;
